<template>
    <div class="Dashboard">
        <!--
        Triggered when the user wishes to navigate to another page

        @event navigate
        @property {object} to The Vue router object or other flag used to trigger navigation
        -->
        <RPSideNav
            :sections="sections"
            :active-section="activeSection"
            class="Dashboard__side-column"
            @navigate="to => $emit('navigate', to)"
        />
        <!--
            Triggered when the user wishes to navigate to their profile page
            @event nav-to-profile
            -->
        <!--
            Triggered when the user wishes to sign out of their account
            @event sign-out
            -->
        <RPTopNav
            class="Dashboard__header"
            :active-section="activeSection"
            :subsections="subsections"
            :active-subsection="activeSubsection"
            :user-profile="userProfile"
            :reports="reports"
            :notifications="notifications"
            @navigate="to => $emit('navigate', to)"
            @nav-to-profile="$emit('nav-to-profile')"
            @sign-out="e => $emit('sign-out', e)"
            @notification-page="$emit('notification-page')"
            @delete-notification="id => $emit('delete-notification', id)"
        >
            <template v-slot:search>
                <slot name="search"></slot>
            </template>
            <template v-slot:profile>
                <slot name="profile"></slot>
            </template>
            <template v-slot:notifications>
                <slot name="notifications"></slot>
            </template>
            <template v-slot:help>
                <slot name="help"></slot>
            </template>
        </RPTopNav>
        <div class="Dashboard__content-container">
            <!-- @slot Default Slot for the main content of the site-->
            <div class="Dashboard__content">
                <slot />
            </div>
            <div class="Dashboard__footer">
                Copyright &copy; 2021 ReviewPush.com. All Rights Reserved.
                <RPLogo class="Dashboard__footer-icon" />
            </div>
        </div>
    </div>
</template>

<script>
import RPLogo from '../../../images/logo/rp-logo-white.svg';
import RPTopNav from 'components/Complex/Navs/RPTopNav.vue';
import RPSideNav from 'components/Complex/Navs/RPSideNav.vue';

export default {
    name: 'RPDashboardLayout',
    components: {
        RPLogo,
        RPTopNav,
        RPSideNav,
    },
    props: {
        /**
        * The primary sections of the RP Site
        * Each section is formatted {name:'', key: '', label: '', icon: '', to: {name: '' }}
        */
        sections: {
            type: Array,
            required: true,
            default: () => ([])
        },
        /**
        * The current active section the user is on
        * The section must be formatted:
        * {name:'', label: '', icon: '', to: {name: '' }}
        */
        activeSection: {
            type: Object,
            required: true,
            default: () => ({
                key: '',
                label: '',
                icon: null,
                to: {name: ''}
            })
        },
        /**
        * The subsections of the section the user is currently on
        * Each subsection is formatted:
        * {name:'', key: '', label: '', to: {name: '' }}
        */
        subsections: {
            type: Array,
            required: true,
            default: () => ([])
        },
        /**
        * The subsection the user is currently on
        * The subsection must be formatted:
        * {name:'', key: '', label: '', to: {name: '' }}
        */
        activeSubsection: {
            type: Object,
            default: () => ({}),
        },
        /**
        * User Profile information used for the User Dropdown
        * Must include:
        * {first_name: '', email: '', profile_image: '', role_name: "" }
        */
        userProfile: {
            type: Object,
            default: () => ({
                first_name: "",
                email: "",
                profile_image: false,
                role_name: "",
            }),
        },
        reports: {
            type: Array,
            default: () => ([])
        },
        notifications: {
            type: Array,
            default: () => ([])
        },
    },
};
</script>
<style lang="scss">
.Dashboard {
    width: inherit;
    height: inherit;
    overflow: hidden;
    grid-template-columns: min-content auto;
    grid-template-rows: 3.5rem auto;
    display: grid;
    background: $grey-50;

    &__side-column {
        grid-column: 1;
        grid-row: span 2;
        @media(max-width: $max-md-width){
            grid-row: 1;
            height: 3.5rem;
        }
    }
    &__header {
        grid-column: 2;
        grid-row: 1;
    }
    &__content-container {
        grid-column: 2;
        grid-row: 2;
        position:relative;
        display: grid;
        grid-template-columns: auto;
        grid-template-rows: auto min-content;
        row-gap: 6rem;
        margin-bottom: 1rem;
        overflow-y: auto;

        @media(max-width: $max-md-width){
            grid-column: span 2;
        }
    }
    &__content {
        max-width: 84rem;
        margin: 0 auto;
        grid-row: 1;
        padding: 1.5rem;
        display: grid;
        grid-template-columns: minmax(0, 84rem);
    }
    &__footer {
        grid-row: 2;
        padding: 0 1.5rem;
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
        color: $grey-300;
        font-weight: 400;
        font-size: 0.85rem;
        line-height: 1rem;
        &-icon {
            height: 1rem;
            margin-right: 0.25rem;
        }
    }
}
</style>

<docs>
    Primary Dashboard Layout for RP.  It contains the Top Nav and Side Nav as well as the User Profiles Dropdown.
    ```[import](./examples/RPDashboardLayoutExample.vue)
    ```
</docs>

